import type {
  WalletTransactionEntity,
  WalletTransactionFilters,
  WalletTransactionStatus,
  WalletTransactionType,
} from "@/modules/dashboard/cards/types/wallets-api.types";
import { isIncomingDirection } from "@/modules/dashboard/cards/utils/walletMovementFormat";
import type { CardWalletsTranslations } from "@/shared/lang/interfaces/cardWallets";

import type { CardWalletMovementsFilters } from "../types/cardWallets.types";
import { isManualAdjustment } from "./cardWalletAdjust";

/**
 * 20 rather than the endpoint's default 10: the table is this page's main content and its paginator sits in
 * the table footer. The backend caps `limit` at 200.
 */
export const MOVEMENTS_PAGE_SIZE = 20;

export const DEFAULT_MOVEMENTS_FILTERS: CardWalletMovementsFilters = {
  transaction_type: "all",
  direction: "all",
  from: "",
  to: "",
};

/**
 * `date_from` / `date_to` are instants (`@IsDateString`), not days, and the backend puts them straight into a
 * `Between` on `created_at`. A bare `YYYY-MM-DD` as the upper bound would mean midnight and drop the whole
 * last day, so each day is widened to its UTC boundaries.
 */
export function dayRangeToInstants(from: string, to: string): { from?: string; to?: string } {
  return {
    from: from ? `${from}T00:00:00.000Z` : undefined,
    to: to ? `${to}T23:59:59.999Z` : undefined,
  };
}

/** The filters as the endpoint takes them. `"all"` is a UI sentinel and is never sent. */
export function toMovementsQuery(
  filters: CardWalletMovementsFilters,
  page: number,
): WalletTransactionFilters {
  const range = dayRangeToInstants(filters.from, filters.to);
  return {
    skip: (page - 1) * MOVEMENTS_PAGE_SIZE,
    limit: MOVEMENTS_PAGE_SIZE,
    ...(filters.transaction_type !== "all" ? { transaction_type: filters.transaction_type } : {}),
    ...(filters.direction !== "all" ? { direction: filters.direction } : {}),
    ...(range.from ? { date_from: range.from } : {}),
    ...(range.to ? { date_to: range.to } : {}),
  };
}

export function hasActiveMovementFilters(filters: CardWalletMovementsFilters): boolean {
  return (
    filters.transaction_type !== "all" ||
    filters.direction !== "all" ||
    filters.from !== "" ||
    filters.to !== ""
  );
}

const TYPE_LABEL_KEYS: Record<WalletTransactionType, keyof CardWalletsTranslations> = {
  TOPUP: "tx_type_topup",
  TRANSFER: "tx_type_transfer",
  WITHDRAWAL: "tx_type_withdrawal",
  FEE: "tx_type_fee",
  DEPOSIT: "tx_type_deposit",
  REVERSAL: "tx_type_reversal",
  ADJUSTMENT: "tx_type_adjustment",
};

/** Falls back to the raw enum value, so a type the backend adds later still renders something true. */
export function movementTypeLabelKey(type: WalletTransactionType): keyof CardWalletsTranslations | null {
  return TYPE_LABEL_KEYS[type] ?? null;
}

const STATUS_LABEL_KEYS: Record<WalletTransactionStatus, keyof CardWalletsTranslations> = {
  PENDING: "tx_status_pending",
  COMPLETED: "tx_status_completed",
  FAILED: "tx_status_failed",
  REVERSED: "tx_status_reversed",
};

export function movementStatusLabelKey(
  status: WalletTransactionStatus,
): keyof CardWalletsTranslations | null {
  return STATUS_LABEL_KEYS[status] ?? null;
}

/**
 * Transaction `description` → label, by EXACT match (lower-cased) on what the backend writes today.
 *
 * That column is free text the services author by hand, and it reaches this table untranslated and in
 * whichever language whoever wrote the line happened to use — `Manual balance adjustment` in English next to
 * `Recarga de wallet` in Spanish, in the same list. Mapping it is the only way the row obeys rule 6; a value
 * outside this map renders verbatim rather than being forced into a wrong label.
 */
const DESCRIPTION_LABEL_KEYS: Record<string, keyof CardWalletsTranslations> = {
  // card-wallets.service.ts → adjustCryptomateFundingWalletBalance (this screen's own write)
  "manual balance adjustment": "tx_desc_manual_adjustment",
  // wallet-webhook-processor.service.ts → a provider deposit credited to the wallet
  "recarga de wallet": "tx_desc_wallet_recharge",
};

/**
 * The same, for the descriptions the backend builds with interpolation (an amount, a card's last four). The
 * label drops those details on purpose: the row already carries the amount in its own column.
 *
 * Prefixes are matched in order, so a new backend description that starts like one of these would be
 * mislabelled — add its own rule above the looser one rather than widening an existing match.
 */
const DESCRIPTION_LABEL_PREFIXES: readonly {
  prefix: string;
  key: keyof CardWalletsTranslations;
}[] = [
  // cards.shared.service.ts, the two legs one crypto deposit writes onto the organization's card wallet
  { prefix: "depósito cripto de", key: "tx_desc_card_crypto_deposit" },
  { prefix: "transferencia de", key: "tx_desc_card_crypto_transfer" },
  // card-wallets.service.ts → transferBetweenCardWallets
  { prefix: "internal wallet transfer", key: "tx_desc_internal_transfer" },
];

/**
 * The i18n key for a movement's description, or `null` to render the backend's own text.
 *
 * Deliberately NOT exhaustive: the pocket movements (`Topup de … a bolsillo`, `Devolución de … desde
 * bolsillo`) are left out because they are written against a POCKET wallet and were not verified to reach
 * this screen. They fall through verbatim, exactly as every description does today, so nothing regresses.
 */
export function movementDescriptionLabelKey(
  description: string | null | undefined,
): keyof CardWalletsTranslations | null {
  const text = description?.trim().toLowerCase();
  if (!text) return null;

  const exact = DESCRIPTION_LABEL_KEYS[text];
  if (exact) return exact;

  return DESCRIPTION_LABEL_PREFIXES.find((rule) => text.startsWith(rule.prefix))?.key ?? null;
}

export function movementStatusVariant(
  status: WalletTransactionStatus,
): "success" | "warning" | "danger" | "neutral" {
  if (status === "COMPLETED") return "success";
  if (status === "PENDING") return "warning";
  if (status === "FAILED") return "danger";
  return "neutral";
}

export type MovementAuthor =
  | { kind: "named"; label: string; title: string }
  | { kind: "you" }
  | { kind: "unavailable" };

/**
 * Who recorded the movement.
 *
 * `created_by` is always `null` on a manual adjustment: the backend's `attachCreatedBy` looks for
 * `metadata.executed_by_user_id` or `metadata.audit_log_id`, and the adjust writes
 * `metadata.updated_by_user_id`. The raw uuid is never shown — the only name this screen can resolve for free
 * is the signed-in operator's own.
 */
export function movementAuthor(
  movement: WalletTransactionEntity,
  currentUserId: string | null,
): MovementAuthor {
  if (movement.created_by?.email) {
    return {
      kind: "named",
      label: movement.created_by.email,
      title: movement.created_by.full_name || movement.created_by.email,
    };
  }
  const recordedBy = movement.metadata?.updated_by_user_id;
  if (currentUserId && typeof recordedBy === "string" && recordedBy === currentUserId) {
    return { kind: "you" };
  }
  return { kind: "unavailable" };
}

/** `true` when the money came in. The wire value is lowercase (`'in'`/`'out'`); the helper normalizes case. */
export function isIncomingMovement(movement: WalletTransactionEntity): boolean {
  return isIncomingDirection(movement.direction);
}

export { isManualAdjustment };
